import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { track } from '../lib/paywall';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CouncilPlans } from '../components/CouncilPlans';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Check, X, ArrowRight, ShieldCheck, Briefcase, Award } from 'lucide-react';
import { toast } from 'sonner';

const fmtGBP = (pence) => {
  if (pence == null) return '—';
  if (pence === 0) return 'Free';
  return `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(true);
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/billing/catalogue').then(({ data }) => setCatalogue(data));
    track('paywall_view', { source: 'pricing_page' });
  }, []);

  const startSubscription = async (planLookup) => {
    track('upgrade_cta_click', { source: 'pricing_page', plan_id: planLookup });
    if (!user) {
      toast.info('Please sign in or create an account to start your trial.');
      navigate('/login?next=/pricing');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/billing/checkout/subscription', {
        plan_lookup: planLookup,
        origin_url: window.location.origin,
      });
      track('checkout_started', { source: 'pricing_page', plan_id: planLookup });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || 'Could not start checkout');
      setLoading(false);
    }
  };

  const startConsultancy = async (kind) => {
    if (!user) {
      toast.info('Sign in to commission a service.');
      navigate('/login?next=/pricing');
      return;
    }
    if (kind === 'audit_self') {
      toast.info('Commission a self-referral audit from your business profile, or use the Commission Basket on the Intervention Planner.');
      navigate('/interventions/matrix');
      return;
    }
    navigate('/interventions/matrix');
  };

  if (!catalogue) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 rounded bg-brand-orange animate-pulse" /></div>;
  }

  const { plans, payg, consultancy } = catalogue;

  return (
    <div className="min-h-screen bg-page-bg" data-testid="pricing-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-ink/10 bg-ink/[0.02]">
        <div className="max-w-[1200px] mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-brand-orange/10 text-brand-orange border-brand-orange/30 hover:bg-brand-orange/15 font-body text-[11px]">
              Adoptic — Tees Valley Digital Register
            </Badge>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink tracking-tight mb-4">
            Pricing built for councils,<br /><span className="text-brand-orange">analysts and businesses.</span>
          </h1>
          <p className="font-body text-base text-ink/60 max-w-2xl leading-relaxed">
            Free, forever, for the public dashboard. Subscribe to unlock the Intervention Planner, Economic Impact Estimator and council-grade reporting. Councils can license a full district from £500/month (+ VAT). Commission B2B2C audits for any of Tees Valley's <strong className="text-ink">37,769</strong> businesses — UKSPF-eligible.
          </p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <Tabs defaultValue="saas">
          <TabsList className="grid grid-cols-2 sm:grid-cols-5 w-full max-w-4xl mb-10 bg-ink/5">
            <TabsTrigger value="saas" data-testid="pricing-tab-saas" className="font-body text-xs sm:text-sm">SaaS Plans</TabsTrigger>
            <TabsTrigger value="councils" data-testid="pricing-tab-councils" className="font-body text-xs sm:text-sm">Council licences</TabsTrigger>
            <TabsTrigger value="consultancy" data-testid="pricing-tab-consultancy" className="font-body text-xs sm:text-sm">Consultancy</TabsTrigger>
            <TabsTrigger value="how" data-testid="pricing-tab-how" className="font-body text-xs sm:text-sm">How it works</TabsTrigger>
            <TabsTrigger value="matrix" data-testid="pricing-tab-matrix" className="font-body text-xs sm:text-sm">Feature matrix</TabsTrigger>
          </TabsList>

          {/* === SaaS === */}
          <TabsContent value="saas">
            <div className="flex items-center justify-center gap-3 mb-8">
              <span className={`font-body text-sm ${!annual ? 'text-ink' : 'text-ink/40'}`}>Monthly</span>
              <Switch checked={annual} onCheckedChange={setAnnual} data-testid="billing-period-toggle" />
              <span className={`font-body text-sm ${annual ? 'text-ink' : 'text-ink/40'}`}>Annual</span>
              {annual && <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30 font-body text-[10px] ml-2">Save 20%</Badge>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Free */}
              <PlanCard
                planKey="free"
                name="Free"
                price="Free"
                period="forever"
                tagline={plans.free.tagline}
                features={['Intelligence Map', 'Business search & profiles', 'Public analytics dashboard', '2 AI queries / day', 'No reports']}
                cta="Start exploring"
                onClick={() => navigate('/map')}
                testId="plan-free"
              />
              {/* Pro */}
              <PlanCard
                planKey="pro"
                name="Pro"
                price={annual ? `£${plans.pro.price_annual_per_month / 100}` : `£${plans.pro.price_monthly / 100}`}
                period={annual ? '/mo, billed annually' : '/month'}
                priceFootnote={annual ? `${fmtGBP(plans.pro.price_annual_total)} / year` : undefined}
                tagline={plans.pro.tagline}
                features={['Everything in Free', 'Intervention Planner', 'Economic Impact Estimator', 'Priority Matrix', '30 AI queries / month', '10 reports / month', 'Standard PDF export', '14-day free trial']}
                cta="Start free trial"
                highlight
                badge="Most popular"
                onClick={() => startSubscription(annual ? 'pro_annual' : 'pro_monthly')}
                disabled={loading}
                testId="plan-pro"
              />
              {/* Premium */}
              <PlanCard
                planKey="premium"
                name="Premium"
                price={annual ? `£${plans.premium.price_annual_per_month / 100}` : `£${plans.premium.price_monthly / 100}`}
                period={annual ? '/mo, billed annually' : '/month'}
                priceFootnote={annual ? `${fmtGBP(plans.premium.price_annual_total)} / year` : undefined}
                tagline={plans.premium.tagline}
                features={['Everything in Pro', '150 AI queries / month', '50 reports / month', 'High-res branded PDF', 'CSV export', 'Council-level reports', 'Sensitivity analysis', '14-day free trial']}
                cta="Start free trial"
                onClick={() => startSubscription(annual ? 'premium_annual' : 'premium_monthly')}
                disabled={loading}
                testId="plan-premium"
              />
              {/* Enterprise */}
              <PlanCard
                planKey="enterprise"
                name="Enterprise"
                price="Custom"
                period="annual contract"
                tagline={plans.enterprise.tagline}
                features={['Everything in Premium', 'Unlimited AI & reports', 'API access', 'Multi-authority dashboard', 'White-label exports', 'Dedicated account manager', 'Council letterhead PDFs']}
                cta="Contact us"
                onClick={() => window.location.href = `mailto:${plans.enterprise.contact_email}?subject=Enterprise%20Enquiry%20%E2%80%94%20Adoptic`}
                testId="plan-enterprise"
              />
            </div>

            {/* PAYG */}
            <div className="mt-12 rounded-[6px] border border-ink/10 bg-white p-6">
              <h3 className="font-display text-lg font-extrabold text-ink mb-1">Pay-as-you-go credits</h3>
              <p className="font-body text-sm text-ink/60 mb-5">Top up beyond your monthly plan allowance — credits never expire within the billing period and stack on top of the included quota.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(payg).map(([k, v]) => (
                  <div key={k} className="rounded-[4px] border border-ink/10 p-4">
                    <p className="font-body text-xs text-ink/50 uppercase tracking-wide mb-1">{v.label}</p>
                    <p className="font-display text-2xl font-extrabold text-ink">£{(v.price / 100).toFixed(2)}</p>
                    <p className="font-body text-[11px] text-ink/40 mt-1">per credit</p>
                  </div>
                ))}
              </div>
              <p className="font-body text-[12px] text-ink/40 mt-4">Available from your <Link to="/account" className="text-brand-orange hover:underline">Account page</Link> after subscribing.</p>
            </div>
          </TabsContent>

          {/* === Council licences (TVCA per-district) === */}
          <TabsContent value="councils">
            <CouncilPlans onSubscribe={startSubscription} loading={loading} />
          </TabsContent>

          {/* === Consultancy === */}
          <TabsContent value="consultancy">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <ConsultancyCard
                icon={ShieldCheck}
                title={consultancy.audit.name}
                price={consultancy.audit.price}
                description={consultancy.audit.description}
                bullets={['Five-parameter digital audit', 'Written audit report (PDF)', 'UKSPF-eligible evidence', 'Per business']}
                cta="Commission an audit"
                onClick={() => startConsultancy('audit')}
                testId="consultancy-audit"
              />
              <ConsultancyCard
                icon={Briefcase}
                title={consultancy.listing.name}
                price={consultancy.listing.price}
                description={consultancy.listing.description}
                bullets={['Audit included', 'Google Business setup', 'Up to 3 directory listings', '30-day score re-assessment']}
                cta="Commission listings"
                onClick={() => startConsultancy('listing')}
                highlight
                testId="consultancy-listing"
              />
              <ConsultancyCard
                icon={Award}
                title={consultancy.uplift.name}
                price={consultancy.uplift.price}
                description={consultancy.uplift.description}
                bullets={['Full intervention package', 'Social + reviews + listings', '30-day & 90-day re-assessment', 'Tier Uplift Certificate (PDF)']}
                cta="Commission full uplift"
                onClick={() => startConsultancy('uplift')}
                testId="consultancy-uplift"
              />
            </div>

            <div className="mt-8 rounded-[6px] border border-ink/10 bg-ink/[0.02] p-6">
              <h3 className="font-display text-lg font-extrabold text-ink mb-2">Council batch commissions</h3>
              <p className="font-body text-sm text-ink/60">Premium and Enterprise customers can multi-select businesses from the Priority Matrix and check out as a basket. Orders of <strong className="text-ink">10+ businesses</strong> are invoiced via Stripe (BACS or card, 30-day terms). UKSPF-eligible evidence is itemised on every invoice.</p>
              <Button onClick={() => navigate('/interventions/matrix')} className="mt-4 bg-ink hover:bg-ink/90 text-white font-body text-sm" data-testid="open-priority-matrix-btn">
                Open Priority Matrix <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </TabsContent>

          {/* === How it works === */}
          <TabsContent value="how">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-8">
              {[
                ['1', 'Identify', 'Filter the Priority Matrix for CRITICAL or HIGH-priority invisible businesses.'],
                ['2', 'Plan', 'Use the Intervention Planner & Economic Impact Estimator to model ROI.'],
                ['3', 'Commission', 'Add to basket: Audit £149, Verification £299, or Full Uplift £499.'],
                ['4', 'Deliver', 'We perform the digital intervention and re-assess the score at 30 days.'],
                ['5', 'Evidence', 'Receive the audit report and Tier Uplift Certificate — UKSPF-ready.'],
              ].map(([n, h, b]) => (
                <div key={n} className="rounded-[6px] border border-ink/10 bg-white p-5">
                  <div className="w-7 h-7 rounded-full bg-brand-orange/10 text-brand-orange font-display font-extrabold text-sm flex items-center justify-center mb-3">{n}</div>
                  <h4 className="font-display text-sm font-extrabold text-ink mb-1">{h}</h4>
                  <p className="font-body text-xs text-ink/60 leading-relaxed">{b}</p>
                </div>
              ))}
            </div>

            <h3 className="font-display text-lg font-extrabold text-ink mb-3">Seven revenue streams</h3>
            <div className="flex flex-wrap gap-2">
              {['SaaS subscriptions', 'PAYG credits', 'Audit fees (£149)', 'Verification fees (£299)', 'Full Uplift (£499)', 'Council batch invoicing', 'Enterprise contracts'].map(p => (
                <Badge key={p} className="bg-ink/5 text-ink/70 border-ink/10 font-body text-xs px-3 py-1.5 hover:bg-ink/10">{p}</Badge>
              ))}
            </div>
          </TabsContent>

          {/* === Feature Matrix === */}
          <TabsContent value="matrix">
            <FeatureMatrix />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PlanCard({ name, price, period, priceFootnote, tagline, features, cta, onClick, highlight, badge, disabled, testId }) {
  return (
    <div className={`rounded-[6px] p-6 flex flex-col ${highlight ? 'bg-ink text-white border border-ink shadow-xl' : 'bg-white border border-ink/10'}`} data-testid={testId}>
      {badge && <Badge className="self-start bg-brand-orange text-white border-0 font-body text-[10px] mb-3">{badge}</Badge>}
      <h3 className={`font-display text-2xl font-extrabold mb-1 ${highlight ? 'text-white' : 'text-ink'}`}>{name}</h3>
      <p className={`font-body text-xs mb-5 ${highlight ? 'text-white/60' : 'text-ink/50'}`}>{tagline}</p>
      <div className="mb-5">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`font-display text-4xl font-extrabold leading-none ${highlight ? 'text-white' : 'text-ink'}`}>{price}</span>
          <span className={`font-body text-xs ${highlight ? 'text-white/60' : 'text-ink/50'}`}>{period}</span>
        </div>
        {priceFootnote && <p className={`font-body text-[11px] mt-1.5 ${highlight ? 'text-white/40' : 'text-ink/40'}`}>{priceFootnote}</p>}
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {features.map((f, i) => (
          <li key={f} className={`flex items-start gap-2 font-body text-xs ${highlight ? 'text-white/80' : 'text-ink/70'}`}>
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-brand-orange" strokeWidth={3} /> {f}
          </li>
        ))}
      </ul>
      <Button
        onClick={onClick}
        disabled={disabled}
        data-testid={`${testId}-cta`}
        className={`w-full font-body text-sm ${highlight ? 'bg-brand-orange hover:bg-brand-orange/90 text-white' : 'bg-ink hover:bg-ink/90 text-white'}`}
      >
        {cta} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  );
}

function ConsultancyCard({ icon: Icon, title, price, description, bullets, cta, onClick, highlight, testId }) {
  return (
    <div className={`rounded-[6px] p-6 ${highlight ? 'bg-ink text-white border border-ink' : 'bg-white border border-ink/10'}`} data-testid={testId}>
      <div className={`w-10 h-10 rounded-[4px] flex items-center justify-center mb-4 ${highlight ? 'bg-brand-orange/20' : 'bg-brand-orange/10'}`}>
        <Icon className="w-5 h-5 text-brand-orange" strokeWidth={1.5} />
      </div>
      <h3 className={`font-display text-xl font-extrabold mb-2 ${highlight ? 'text-white' : 'text-ink'}`}>{title}</h3>
      <p className={`font-body text-xs mb-4 leading-relaxed ${highlight ? 'text-white/60' : 'text-ink/55'}`}>{description}</p>
      <p className={`font-display text-3xl font-extrabold mb-1 ${highlight ? 'text-white' : 'text-ink'}`}>£{(price / 100).toLocaleString('en-GB')}</p>
      <p className={`font-body text-[11px] mb-5 ${highlight ? 'text-white/50' : 'text-ink/40'}`}>per business · one-time</p>
      <ul className="space-y-1.5 mb-5">
        {bullets.map((b, i) => (
          <li key={b} className={`flex items-start gap-2 font-body text-xs ${highlight ? 'text-white/80' : 'text-ink/70'}`}>
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-brand-orange" strokeWidth={3} /> {b}
          </li>
        ))}
      </ul>
      <Button onClick={onClick} data-testid={`${testId}-cta`} className={`w-full font-body text-sm ${highlight ? 'bg-brand-orange hover:bg-brand-orange/90 text-white' : 'bg-ink hover:bg-ink/90 text-white'}`}>
        {cta} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  );
}

function FeatureMatrix() {
  const rows = [
    ['Intelligence Map — all business pins', '✓', '✓', '✓', '✓'],
    ['Business search & profiles', '✓', '✓', '✓', '✓'],
    ['Public analytics dashboard', '✓', '✓', '✓', '✓'],
    ['AI Query', '2 / day', '30 / mo', '150 / mo', 'Unlimited'],
    ['Download / share AI response', '✗', '✓', '✓', '✓'],
    ['Intervention Planner', '✗', '✓', '✓', '✓'],
    ['Economic Impact Estimator', '✗', 'Ranges', 'Ranges + sensitivity', 'Full + bespoke'],
    ['Priority Matrix', '✗', '✓', '✓', '✓'],
    ['Reports / month', '✗', '10', '50', 'Unlimited'],
    ['PDF quality', 'Lock', 'Standard', 'High-res branded', 'Publication-grade'],
    ['CSV export', '✗', '✗', '✓', '✓'],
    ['Council-level full report', '✗', '✗', '✓', '✓'],
    ['White-label exports', '✗', '✗', '✗', '✓'],
    ['API access', '✗', '✗', '✗', '✓'],
    ['Multi-authority dashboard', '✗', '✗', '✗', '✓'],
    ['PAYG — AI prompt top-up', '✗', '£5/prompt', '£3/prompt', 'N/A'],
    ['PAYG — report top-up', '✗', '£8/report', '£5/report', 'N/A'],
    ['14-day free trial', 'N/A', '✓', '✓', 'N/A'],
  ];
  return (
    <div className="rounded-[6px] border border-ink/10 bg-white overflow-x-auto" data-testid="feature-matrix">
      <table className="w-full text-left">
        <thead className="bg-ink/[0.03] border-b border-ink/10">
          <tr>
            <th className="font-display text-xs font-extrabold text-ink p-4">Feature</th>
            <th className="font-display text-xs font-extrabold text-ink p-4">Free</th>
            <th className="font-display text-xs font-extrabold text-ink p-4">Pro</th>
            <th className="font-display text-xs font-extrabold text-ink p-4">Premium</th>
            <th className="font-display text-xs font-extrabold text-ink p-4">Enterprise</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row[0]} className="border-b border-ink/5 last:border-0">
              <td className="font-body text-xs text-ink/80 p-4">{row[0]}</td>
              {row.slice(1).map((cell, j) => (
                <td key={j} className="font-body text-xs text-ink/70 p-4">
                  {cell === '✓' ? <Check className="w-4 h-4 text-emerald-600" /> : cell === '✗' ? <X className="w-4 h-4 text-ink/30" /> : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
